// src/services/campaign.service.ts
import axios from "axios";

/* ---------------- Types ---------------- */

export type ObjectIdString = string;

export interface ContactList {
  _id: ObjectIdString;
  businessId: string;
  name: string;
  description?: string;
  columns?: string[];
  memberCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ImportJob {
  _id: ObjectIdString;
  businessId: string;
  listId?: ObjectIdString | null;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED";
  totalRows: number;
  successCount: number;
  duplicateCount?: number;
  invalidCount: number;
  errorsSample?: Array<{ row: number; reason: string }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface TemplateComponentParam {
  type: "text" | "image" | "video" | "document";
  text?: string;
  parameter_name?: string;
  image?: { link?: string; id?: string };
  video?: { link?: string; id?: string };
  document?: { link?: string; id?: string };
}

export interface TemplateComponent {
  type: "body" | "header";
  parameters: TemplateComponentParam[];
}

export interface TemplateConfig {
  name: string;
  language: string; // e.g. "en_US"
  components?: TemplateComponent[];
  headerMediaUrl?: string;
  headerMediaId?: string;
  variableMappings?: TemplateVariableMapping[];
}

export interface TemplateVariableMapping {
  parameterName: string;
  source: "COLUMN" | "STATIC";
  column?: string;
  value?: string;
  fallback?: string;
}

export type FollowUpAudience = "ALL" | "INTERESTED";
export type FollowUpDelayUnit = "MINUTES" | "HOURS" | "DAYS";
export type CampaignBatchIntervalUnit = "HOURS" | "DAYS";

export interface CampaignBatchingConfig {
  enabled: boolean;
  batchSize: number;
  intervalAmount: number;
  intervalUnit: CampaignBatchIntervalUnit;
  scheduleMode: "INTERVAL" | "CUSTOM_DATES" | "PARENT_SCHEDULE";
  customSchedule: string[];
}

export interface CampaignListRange {
  listId: ObjectIdString;
  start: number;
  end: number;
}

export interface CampaignDeliveryBatch {
  id: string;
  number: number;
  recipientCount: number;
  scheduledAt: string;
  status: string;
  failedCount?: number;
  completedAt?: string | null;
}

export interface FollowUpStepConfig {
  delayAmount: number;
  delayUnit: FollowUpDelayUnit;
  audience: FollowUpAudience;
  template: TemplateConfig;
}

export interface FollowUpConfig {
  enabled: boolean;
  steps: FollowUpStepConfig[];
}

export interface CampaignFollowUpJob {
  _id: ObjectIdString;
  campaignId: ObjectIdString;
  step: number;
  audience: FollowUpAudience;
  template: TemplateConfig;
  delayAmount: number;
  delayUnit: FollowUpDelayUnit;
  scheduledAt?: string | null;
  estimatedScheduledAt?: string | null;
  status: string;
  recipientCount?: number;
  sentCount?: number;
  failedCount?: number;
  metrics?: Record<string, number>;
  activity?: CampaignActivity[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignActivity {
  action: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

export interface CampaignCreatePayload {
  // NOTE: uuid is businessId for campaign module (as per your current codebase)
  uuid: string;
  name: string;
  listIds: ObjectIdString[];
  template: TemplateConfig;
  scheduledAt?: string | null;
  followUps?: FollowUpConfig;
  batching?: CampaignBatchingConfig;
  listRanges?: CampaignListRange[];
}

export interface CampaignDoc {
  _id: ObjectIdString;
  businessId: string;
  name: string;
  listIds: ObjectIdString[];
  lists?: Array<{ id: ObjectIdString; name: string }>;
  template: TemplateConfig;
  status: string;
  scheduledAt?: string | null;
  followUps?: FollowUpConfig;
  batching?: CampaignBatchingConfig;
  listRanges?: CampaignListRange[];
  deliveryBatches?: CampaignDeliveryBatch[];
  followUpJobs?: CampaignFollowUpJob[];
  nextFollowUpAt?: string | null;
  followUpsCompleted?: number;
  metrics?: Record<string, number>;
  crmMetrics?: {
    interested: number;
    won: number;
  };
  activity?: CampaignActivity[];
  createdAt?: string;
  updatedAt?: string;
}

export type CampaignMessageStatus =
  | "QUEUED"
  | "SENT"
  | "DELIVERED"
  | "READ"
  | "FAILED";

export interface CampaignMessageError {
  code?: number;
  title?: string;
  message?: string;
  href?: string;
  error_data?: {
    details?: string;
  };
}

export interface CampaignMessageDoc {
  _id: ObjectIdString;
  businessId: string;
  campaignId: ObjectIdString;
  followUpId?: ObjectIdString;
  followUpStep?: number;
  messageType?: "FOLLOW_UP";
  listId: ObjectIdString;
  contactId: ObjectIdString;
  contactName?: string | null;
  contactAttributes?: Record<string, string>;
  batchNumber?: number;
  listName?: string | null;
  to: string;
  status: CampaignMessageStatus;
  waMessageId?: string | null;
  error?: CampaignMessageError | null;
  lastError?: CampaignMessageError | null;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  failedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/* ---------------- BASE CONFIG ---------------- */

// NOTE: trailing slash is OK; we’ll keep paths relative
const BASE_URL =
  process.env.REACT_APP_PYTHON_BACKEND_URL || "http://localhost:8000/";

/**
 * If your backend routes are like:
 *   /api/lists
 *   /api/contacts/import-csv
 *   /api/imports/:id
 *   /api/campaigns
 *
 * Keep API_BASE = "api"
 */
const API_BASE = "api";

/* ---------------- AXIOS INSTANCE ---------------- */

const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

/* ---------------- Services ---------------- */
// NOTE: uuid == businessId (for campaign feature only)

/* ---------- Lists ---------- */

export const createList = async (
  uuid: string,
  name: string,
  description?: string,
): Promise<{ id: string }> => {
  const res = await api.post(`${API_BASE}/lists`, {
    uuid,
    name,
    description: description ?? "",
  });
  return res.data;
};

export const getLists = async (uuid: string): Promise<ContactList[]> => {
  const res = await api.get(`${API_BASE}/lists`, { params: { uuid } });
  return res.data;
};

/* ---------- Contacts import ---------- */

export const importContactsCsv = async (
  uuid: string,
  listId: string | undefined,
  file: File,
  defaultCountry: string = "IN",
): Promise<{
  importId: string;
  listId: string;
  listName: string;
  listCreated: boolean;
}> => {
  const fd = new FormData();
  fd.append("file", file);

  const res = await api.post(`${API_BASE}/contacts/import-csv`, fd, {
    params: { uuid, defaultCountry, ...(listId ? { listId } : {}) },
    headers: { "Content-Type": "multipart/form-data" },
  });

  return res.data;
};

export const getImport = async (
  uuid: string,
  importId: string,
): Promise<ImportJob> => {
  const res = await api.get(`${API_BASE}/imports/${importId}`, {
    params: { uuid },
  });
  return res.data;
};

/* ---------- Campaigns ---------- */

export const createCampaign = async (
  payload: CampaignCreatePayload,
): Promise<{ id: string }> => {
  const res = await api.post(`${API_BASE}/campaigns`, payload);
  return res.data;
};

export const listCampaigns = async (uuid: string): Promise<CampaignDoc[]> => {
  const res = await api.get(`${API_BASE}/campaigns`, { params: { uuid } });
  return res.data;
};

export const getCampaign = async (
  uuid: string,
  id: string,
): Promise<CampaignDoc> => {
  const res = await api.get(`${API_BASE}/campaigns/${id}`, {
    params: { uuid },
  });
  return res.data;
};

export const waitForCampaignReady = async (
  uuid: string,
  id: string,
  timeoutMs: number = 120000,
  pollIntervalMs: number = 500,
): Promise<CampaignDoc> => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const campaign = await getCampaign(uuid, id);
    const status = String(campaign.status || "").toUpperCase();

    if (status === "READY" || status === "SCHEDULED") return campaign;
    if (status === "FAILED") {
      throw new Error("Campaign recipient preparation failed");
    }

    await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs));
  }

  throw new Error("Campaign preparation timed out. Please try again.");
};

export const prepareCampaign = async (uuid: string, id: string) => {
  const res = await api.post(`${API_BASE}/campaigns/${id}/prepare`, null, {
    params: { uuid },
  });
  return res.data;
};

export const startCampaign = async (uuid: string, id: string) => {
  const res = await api.post(`${API_BASE}/campaigns/${id}/start`, null, {
    params: { uuid },
  });
  return res.data;
};

export const retryFailedCampaignMessages = async (uuid: string, id: string) => {
  const res = await api.post(`${API_BASE}/campaigns/${id}/retry-failed`, null, {
    params: { uuid },
  });
  return res.data as { status: string; retried: number };
};

export const retryQueuedCampaignMessages = async (uuid: string, id: string) => {
  const res = await api.post(`${API_BASE}/campaigns/${id}/retry-queued`, null, {
    params: { uuid },
  });
  return res.data as { status: string; retried: number };
};

export const updateCampaign = async (
  uuid: string,
  id: string,
  patch: Partial<Pick<CampaignCreatePayload, "name" | "listIds" | "template">>,
) => {
  const res = await api.patch(`${API_BASE}/campaigns/${id}`, patch, {
    params: { uuid },
  });
  return res.data;
};

export const rescheduleCampaign = async (
  uuid: string,
  id: string,
  scheduledAt: string,
) => {
  const res = await api.patch(
    `${API_BASE}/campaigns/${id}/schedule`,
    { scheduledAt },
    { params: { uuid } },
  );
  return res.data;
};

const campaignAction = async (uuid: string, id: string, action: string) => {
  const res = await api.post(`${API_BASE}/campaigns/${id}/${action}`, null, {
    params: { uuid },
  });
  return res.data;
};

export const pauseCampaign = (uuid: string, id: string) =>
  campaignAction(uuid, id, "pause");
export const resumeCampaign = (uuid: string, id: string) =>
  campaignAction(uuid, id, "resume");
export const cancelCampaign = (uuid: string, id: string) =>
  campaignAction(uuid, id, "cancel");
export const duplicateCampaign = (uuid: string, id: string) =>
  campaignAction(uuid, id, "duplicate") as Promise<{ id: string; status: string }>;

export const deleteCampaign = async (uuid: string, id: string) => {
  const res = await api.delete(`${API_BASE}/campaigns/${id}`, {
    params: { uuid },
  });
  return res.data;
};

export const testSendCampaign = async (uuid: string, id: string, to: string) => {
  const res = await api.post(
    `${API_BASE}/campaigns/${id}/test-send`,
    { to },
    { params: { uuid } },
  );
  return res.data as { status: string; waMessageId?: string };
};

export type FollowUpMutation = {
  delayAmount?: number;
  delayUnit?: FollowUpDelayUnit;
  audience?: FollowUpAudience;
  template?: TemplateConfig;
  scheduledAt?: string;
};

export const addCampaignFollowUp = async (
  uuid: string,
  campaignId: string,
  payload: Required<Pick<FollowUpMutation, "delayAmount" | "delayUnit" | "audience" | "template">> &
    Pick<FollowUpMutation, "scheduledAt">,
) => {
  const res = await api.post(`${API_BASE}/campaigns/${campaignId}/followups`, payload, {
    params: { uuid },
  });
  return res.data as { id: string; status: string };
};

export const getCampaignFollowUp = async (
  uuid: string,
  campaignId: string,
  followUpId: string,
): Promise<CampaignFollowUpJob> => {
  const res = await api.get(
    `${API_BASE}/campaigns/${campaignId}/followups/${followUpId}`,
    { params: { uuid } },
  );
  return res.data;
};

export const updateCampaignFollowUp = async (
  uuid: string,
  campaignId: string,
  followUpId: string,
  payload: FollowUpMutation,
) => {
  const res = await api.patch(
    `${API_BASE}/campaigns/${campaignId}/followups/${followUpId}`,
    payload,
    { params: { uuid } },
  );
  return res.data;
};

const followUpAction = async (
  uuid: string,
  campaignId: string,
  followUpId: string,
  action: string,
) => {
  const res = await api.post(
    `${API_BASE}/campaigns/${campaignId}/followups/${followUpId}/${action}`,
    null,
    { params: { uuid } },
  );
  return res.data;
};

export const pauseCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "pause");
export const resumeCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "resume");
export const cancelCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "cancel");
export const retryFailedCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "retry-failed");
export const retryQueuedCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "retry-queued");
export const duplicateCampaignFollowUp = (uuid: string, campaignId: string, followUpId: string) =>
  followUpAction(uuid, campaignId, followUpId, "duplicate");

export const deleteCampaignFollowUp = async (
  uuid: string,
  campaignId: string,
  followUpId: string,
) => {
  const res = await api.delete(
    `${API_BASE}/campaigns/${campaignId}/followups/${followUpId}`,
    { params: { uuid } },
  );
  return res.data;
};

export const reorderCampaignFollowUps = async (
  uuid: string,
  campaignId: string,
  followUpIds: string[],
) => {
  const res = await api.patch(
    `${API_BASE}/campaigns/${campaignId}/followups/reorder`,
    { followUpIds },
    { params: { uuid } },
  );
  return res.data;
};

export const getCampaignFollowUpMessages = async (
  uuid: string,
  campaignId: string,
  followUpId: string,
  status?: CampaignMessageStatus,
  page: number = 1,
  limit: number = 50,
): Promise<CampaignMessageDoc[]> => {
  const res = await api.get(
    `${API_BASE}/campaigns/${campaignId}/followups/${followUpId}/messages`,
    { params: { uuid, status, page, limit } },
  );
  return res.data;
};

export const getCampaignMessages = async (
  uuid: string,
  id: string,
  status?: CampaignMessageStatus,
  page: number = 1,
  limit: number = 50,
  batchNumbers?: number[],
): Promise<CampaignMessageDoc[]> => {
  const res = await api.get(`${API_BASE}/campaigns/${id}/messages`, {
    params: {
      uuid,
      status,
      page,
      limit,
      batchNumbers: batchNumbers?.length ? batchNumbers.join(",") : undefined,
    },
  });
  return res.data;
};

export interface ListMember {
  id: string;
  phoneE164: string;
  name?: string | null;
  attributes?: Record<string, string>;
}

export const getListMembers = async (
  uuid: string,
  listId: string,
  page: number = 1,
  limit: number = 50,
): Promise<ListMember[]> => {
  const res = await api.get(`/api/lists/${listId}/members`, {
    params: { uuid, page, limit },
  });
  return res.data;
};
